/**
 * A starter's declared fields, as a form, and what is typed back into them.
 *
 * Separate from the screen because it is the half that can be wrong quietly: a
 * column name the service refuses, a percentage stored a hundred times too large,
 * a nested field flattened onto the wrong key. None of those is an error — each is
 * a document with the wrong thing printed on it — so they are pure functions with
 * a test, and the screen is the part that only draws boxes.
 */
import type { FieldSchema } from "@broadpaper/core";

/** One thing to type: a declared field, flattened out of the starter's schema. */
export interface Slot {
  /** The dotted path into the data source: `client.name`. */
  path: string;
  /** The column it becomes: `clientName`. */
  column: string;
  label: string;
  group: string | null;
  schema: FieldSchema;
  required: boolean;
}

/** A repeating field — an invoice's lines — and the columns each row holds. */
export interface ListSlot {
  path: string;
  label: string;
  columns: Slot[];
}

/**
 * A column name from a path.
 *
 * The service refuses anything the expression tokeniser would, so `client.name`
 * cannot stay as it is. Capitalising the segment after each dot keeps it readable
 * in the data screen, where these become real column headings somebody will see.
 */
export function columnName(path: string): string {
  return path.replace(/\.([a-z])/gi, (_, c: string) => c.toUpperCase()).replace(/[^A-Za-z0-9_$]/g, "");
}

/** Everything a starter declares, as a form. Objects are flattened; lists are kept whole. */
export function slotsOf(schema: Record<string, FieldSchema>, prefix = "", group: string | null = null): { fields: Slot[]; lists: ListSlot[] } {
  const fields: Slot[] = [];
  const lists: ListSlot[] = [];

  for (const [key, field] of Object.entries(schema)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const label = field.label ?? key;

    if (field.type === "object" && field.fields) {
      const inner = slotsOf(field.fields, path, label);
      fields.push(...inner.fields);
      lists.push(...inner.lists);
      continue;
    }

    if (field.type === "array") {
      const item = field.itemSchema;
      if (item?.type !== "object" || !item.fields) continue;
      lists.push({
        path,
        label,
        columns: Object.entries(item.fields)
          .filter(([, f]) => f.type !== "object" && f.type !== "array")
          .map(([k, f]) => ({
            path: `${path}.${k}`,
            column: columnName(`${path}.${k}`),
            label: f.label ?? k,
            group: label,
            schema: f,
            required: Boolean(f.required)
          }))
      });
      continue;
    }

    fields.push({ path, column: columnName(path), label, group, schema: field, required: Boolean(field.required) });
  }

  return { fields, lists };
}

/** What kind of box a field gets, and how what is typed into it is read back. */
export function inputKind(schema: FieldSchema): { type: string; step?: string; suffix?: string } {
  switch (schema.type) {
    case "number":
      return { type: "number", step: "any" };
    case "currency":
      return { type: "number", step: "0.01" };
    case "percent":
      return { type: "number", step: "any", suffix: "%" };
    case "date":
      return { type: "date" };
    case "datetime":
      return { type: "datetime-local" };
    default:
      return { type: "text" };
  }
}

/**
 * A typed string, as the value the column holds.
 *
 * A percentage is divided here and nowhere else: the box says `%` beside it, so
 * somebody types 20 and means a fifth. It matches what the matching screen does
 * with a typed rate, which is what keeps a VAT rate the same number wherever it
 * was entered.
 */
export function valueOf(raw: string, schema: FieldSchema): unknown {
  const text = raw.trim();
  if (!text) return null;
  if (schema.type === "number" || schema.type === "currency") {
    const n = Number(text);
    return Number.isFinite(n) ? n : null;
  }
  if (schema.type === "percent") {
    const n = Number(text);
    return Number.isFinite(n) ? Math.round((n / 100) * 1e6) / 1e6 : null;
  }
  return text;
}
